/**
 * Frontend Entry Point
 * React application with TypeScript and Vite
 */

import "./i18n";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import React from "react";
import ReactDOM from "react-dom/client";
import App from "./App";
import "./assets/styles/index.css"; // Single source of truth for all styles
import { queryClient } from "@/lib/query-client";
import { ErrorBoundary } from "./components/ui/error-boundary";
import { initPostHog } from "./lib/posthog";
import { initThemeMode } from "./lib/theme-mode";

// Google client id is optional — empty when Google sign-in is not configured
// for this environment (the GoogleSignInButton renders nothing in that case).
const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID ?? "";

// Initialize PostHog before any render so an early render-time error or
// the very first $pageview is captured. Doing this in App.tsx's useEffect
// would miss errors thrown during the initial render commit.
initPostHog();

// Keeps the React-side mode store in step with the class index.html set,
// and follows the OS setting while the choice is "system".
initThemeMode();

// Dev builds only: the heading font preview (?headingFont=jakarta). The
// dynamic import is in a dead branch of a production build, so the bundle
// does not include the module.
if (import.meta.env.DEV) {
	void import("@/features/styleguide/heading-font-preview").then((module) => {
		module.initHeadingFontPreview();
	});
}

const rootElement = document.getElementById("root");
if (!rootElement) {
	throw new Error("Failed to find the root element");
}
ReactDOM.createRoot(rootElement).render(
	<React.StrictMode>
		<GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
			<QueryClientProvider client={queryClient}>
				<App />
				{import.meta.env.DEV && (
					<ErrorBoundary fallback={<span />}>
						<ReactQueryDevtools initialIsOpen={false} />
					</ErrorBoundary>
				)}
			</QueryClientProvider>
		</GoogleOAuthProvider>
	</React.StrictMode>,
);
